import React, { useState } from 'react';
import classNameBind from 'classnames/bind';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import Popover from '../Popover';
import { useTranslation } from '../Translator';
import { useBlur } from '../../hooks/useBlur';
import styles from './CustomDimensionPicker.module.css';
import { CustomDimensionsList } from './components/CustomDimensionsList';
import { PickerButton } from '../PickerButton';
import { KEYS } from '../../utils/methods/keyboardKeys';

const bindClasses = classNameBind.bind(styles);

const CustomDimensionPicker = ({
  name,
  dimensions,
  dataId = 'CustomDimensionPicker',
  disabled = false,
  error = null,
  onBlur = () => {},
  onChange = () => {},
  onOpen = () => {},
  onSearch = () => {},
  search = '',
  value = null,
  icon = 'box',
} = {}) => {
  const [selected, setSelected] = useState(value);
  const { t } = useTranslation('components');
  const { ref, handleBlur } = useBlur({ name, value, onBlur, disabled });

  const handleChange = (newValue) => {
    if (!isEqual(newValue.value, value)) {
      onChange({
        target: {
          name,
          value: newValue.value,
        },
      });
    }
  };

  const getSelectedId = () => {
    const selectedValue = selected || value;

    return get(dimensions.find(item => isEqual(item.value, selectedValue)), 'id');
  };

  const isInsideTheRange = (index, items) => (
    index >= 0 && index < items.length
  );

  const handleKeyDown = (event, onToggle, isOpen) => {
    const selectedValue = selected || value;

    let index = dimensions.findIndex(item => isEqual(item.value, selectedValue));

    if (
      ((event.key === KEYS.TAB || event.key === KEYS.ESC) && isOpen)
      || (!isOpen && event.key === KEYS.SPACE)) {
      // open/close popover on tab/space
      event.preventDefault();
      onToggle();

      if (!isOpen) {
        onOpen(event);
      }
    }

    if (event.key === KEYS.ENTER) { // clear search, close popover and submit
      event.preventDefault();
      handleChange(dimensions[index]);
      onToggle();
    }

    if ([KEYS.DOWN, KEYS.UP].includes(event.key)) { // move between the items
      event.preventDefault();

      if (!isOpen) {
        onToggle();
      }

      if (event.key === KEYS.DOWN) {
        index += 1;
      } else if (event.key === KEYS.UP) {
        index -= 1;
      }

      if (isInsideTheRange(index, dimensions)) {
        setSelected(dimensions[index].value);
      }
    }

    return null;
  };

  const handleSearchChange = (event, isOpen) => {
    if (isOpen) {
      onSearch({
        target: {
          value: event.target.value,
        },
      });
    }
  };

  return (
    <div
      className={bindClasses('wrapper')}
      ref={ref}
      onBlur={handleBlur}
      data-ppuc-id={dataId}
    >
      <div className={bindClasses('inner-wrapper')}>
        <Popover
          speechBubble
          onOpen={onOpen}
          position="bottom"
          focusOrder={['content']}
          activatorElement={(
            <PickerButton
              icon={{ type: 'icon', content: icon }}
              dataId={dataId}
              disabled={disabled}
              invalid={Boolean(error)}
              tooltip={value ? { content: value.name } : undefined}
            >
              {value ? (
                <div className={bindClasses('selected-dimension')}>
                  {value.name && (
                    <div className={bindClasses('selected-dimension-name', { inactive: !value.active })}>
                      {value.name}
                    </div>
                  )}
                  <div className={bindClasses('selected-dimension-id')}>
                    {`ID: ${value.id}`}
                  </div>
                </div>
              ) : t('CustomDimensionPicker.dimension')}
            </PickerButton>
          )}
          fixedWidth
          childToggle={{ onItemSelected: () => true }}
          disabled={disabled}
          noPadding
          forceRecalculateOnSafari
          onKeyDown={handleKeyDown}
          dataId={`${dataId}.popover`}
        >
          <CustomDimensionsList
            dimensions={dimensions}
            onItemSelected={handleChange}
            search={search}
            onSearchChange={handleSearchChange}
            onSearchKeyDown={handleKeyDown}
            current={getSelectedId()}
            dataId={`${dataId}.DimensionsList`}
          />
        </Popover>
      </div>
    </div>
  );
};

export { CustomDimensionPicker };
